import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { conformToMask } from 'react-text-mask'
import { Translate, withLocalize } from 'react-localize-redux'
import cx from 'classnames'
import { useForm, Controller } from 'react-hook-form'

import { makeStyles } from '@material-ui/core/styles'
import { Typography, FormControlLabel, Checkbox } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import IconButton from '@material-ui/core/IconButton'
import CloseIcon from '@material-ui/icons/Close'
import Select from 'common/components/Select'
import styles from './AddSystemModalForm.module.scss'
import Languages from 'constants/Languages'
import * as BuildingFormJson from 'constants/building'
import { deviceIdMask } from 'constants/DeviceIdMask'
import Button from 'common/components/Button/Button'
import { blue } from '@material-ui/core/colors'
import TextInput from 'common/components/TextInput/TextInput'
import InfoIcon from 'common/components/InfoIcon/InfoIcon'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { ArrowIcon } from 'icons'

export const DEVICE_LOOKUP_ID = 'id'
export const DEVICE_LOOKUP_SN = 'sn'
export const SERIAL_NUMBER_MAX_LENGTH = 8
const SERIAL_NUMBER_DOCS_URL =
  'https://docs.inbiotair.com/support/shipping-ordering-warranty/locate-serial-number-mac-address'

export const serialNumberIssue = identifier => {
  const value = typeof identifier === 'string' ? identifier.trim() : ''
  if (value === '') return 'required'
  if (!/^\d{1,8}$/.test(value)) return 'digits'
  return null
}

export const isDeviceIdentifierValid = (lookup, identifier) => {
  if (lookup === DEVICE_LOOKUP_SN) return serialNumberIssue(identifier) === null
  return typeof identifier === 'string' && identifier.length === 14
}

export const toDeviceListEntry = (lookup, identifier, name) => {
  if (lookup === DEVICE_LOOKUP_SN) {
    return {
      key: identifier.trim(),
      entry: { name, lookup: DEVICE_LOOKUP_SN }
    }
  }
  return {
    key: identifier.toUpperCase(),
    entry: { name, lookup: DEVICE_LOOKUP_ID }
  }
}

const useStyles = makeStyles(() => ({
  input: {
    [`& fieldset`]: {
      borderRadius: 30,
      borderColor: 'grey'
    },
    '& label.Mui-focused': {
      color: blue[600]
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: 'grey'
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: 'grey'
      },
      '&:hover fieldset': {
        borderColor: 'grey'
      },
      '&.Mui-focused fieldset': {
        borderColor: blue[600]
      }
    }
  }
}))

export const AddSystemModalForm = props => {
  const classes = useStyles()
  const {
    isSubmitting,
    initialValues,
    activeLanguage,
    translate,
    submitError,
    onSubmit,
    projects,
    systemFailed,
    user
  } = props
  const [macAddressField, setMacAddress] = useState('')
  const [lookupType, setLookupType] = useState(DEVICE_LOOKUP_ID)
  const [nameField, setNameField] = useState('')
  const { building } = initialValues
  const [activeTab, setActiveTab] = useState(0)
  const [useType, setUseType] = useState('')
  const [openInfo, setOpenInfo] = useState({})
  const [infoTab, setInfoTab] = useState('')
  const [deviceList, setDeviceList] = useState({})
  const [selectedDevices, setSelectedDevices] = useState([])
  const { handleSubmit, errors, control, setValue, register, setError, clearErrors } = useForm()
  const [typeBuildingOptionsSelect, setTypeBuildingOptionsSelect] = useState()
  const [typeOccupancyOptions, setTypeOccupancyOptions] = useState()
  const [typeVentilationOptions, setTypeVentilationOptions] = useState()
  const [isCheckAllDevices, setIsCheckAllDevices] = useState(false)
  const [checkAllDevicesIsClicked, setCheckDevicesAllIsClicked] = useState(false)

  useEffect(() => {
    if (systemFailed?.length > 0) {
      setDeviceList(current => {
        const next = {}
        for (const deviceId in current) {
          if (systemFailed.includes(deviceId)) {
            next[deviceId] = current[deviceId]
          }
        }
        return next
      })
    }
    if (submitError?.res?.status === 409) {
      setActiveTab(0)
    }
  }, [systemFailed])

  const lang = Languages.find(l => l.code === activeLanguage.code).code

  const projectsOptions = Object.values(projects.toJS()).map(project => ({
    value: project.ProjectId,
    label: project.Meta?.name || ''
  }))

  const handleIdentifierChange = event => {
    if (lookupType === DEVICE_LOOKUP_SN) {
      setMacAddress(event.target.value.slice(0, SERIAL_NUMBER_MAX_LENGTH))
      return
    }
    const macAddressParse = conformToMask(event.target.value, deviceIdMask, {
      guide: false
    })
    setMacAddress(macAddressParse.conformedValue)
  }

  const handleLookupChange = nextLookup => {
    const lookup = nextLookup === DEVICE_LOOKUP_SN ? DEVICE_LOOKUP_SN : DEVICE_LOOKUP_ID
    if (lookup === lookupType) return
    setLookupType(lookup)
    setMacAddress('')
    clearErrors('macAddressLength')
  }

  const handleNameField = event => {
    setNameField(event.target.value)
  }

  const typeBuildingOptions = BuildingFormJson.typeOfBuilding.map(c => ({
    label: c[lang],
    value: c.value
  }))

  const antiquityOptions = BuildingFormJson.antiquity.map(c => ({
    label: c[lang],
    value: c.value
  }))

  const airtightnessOptions = BuildingFormJson.airTightness.map(c => ({
    label: c[lang],
    value: c.value
  }))

  const occupancyResidentialOptions = BuildingFormJson.occupancyResidential.map(c => ({
    label: c[lang],
    value: c.value
  }))

  const occupancyTertiaryOptions = BuildingFormJson.occupancyTertiary.map(c => ({
    label: c[lang],
    value: c.value
  }))

  const typeBuildingTertiaryOptions = BuildingFormJson.typeBuildingTertiary.map(c => ({
    label: c[lang],
    value: c.value
  }))

  const typeBuildingResidentialOptions = BuildingFormJson.typeBuildingResidential.map(c => ({
    label: c[lang],
    value: c.value
  }))

  // const typeOfSystemOptions = BuildingFormJson.typeOfSystem.map(c => ({
  //   label: c[lang],
  //   value: c.value
  // }))

  const ventilationResidentialOptions = BuildingFormJson.ventilationResidential.map(c => ({
    label: c[lang],
    value: c.value
  }))

  const ventilationTertiaryOptions = BuildingFormJson.ventilationTertiary.map(c => ({
    label: c[lang],
    value: c.value
  }))

  let initialBuildingType,
    initialVentilation,
    initialOccupancy,
    initialAntiquity,
    initialAirThightness
  // initialTypeSystem

  if (building && building.use.useType) {
    initialBuildingType = BuildingFormJson.typeBuildingResidential.find(
      c => c.value === building.use.buildingType
    )
    if (!initialBuildingType)
      initialBuildingType = BuildingFormJson.typeBuildingTertiary.find(
        c => c.value === building.use.buildingType
      )
    initialBuildingType = {
      label: initialBuildingType[lang],
      value: initialBuildingType.value
    }

    initialOccupancy = BuildingFormJson.occupancyResidential.find(
      c => c.value === building.use.occupation
    )
    if (!initialOccupancy)
      initialOccupancy = BuildingFormJson.occupancyTertiary.find(
        c => c.value === building.use.occupation
      )
    initialOccupancy = {
      label: initialOccupancy[lang],
      value: initialOccupancy.value
    }

    initialVentilation = BuildingFormJson.ventilationResidential.find(
      c => c.value === building.use.ventilation
    )
    if (!initialVentilation)
      initialVentilation = BuildingFormJson.ventilationTertiary.find(
        c => c.value === building.use.ventilation
      )
    initialOccupancy = {
      label: initialOccupancy[lang],
      value: initialOccupancy.value
    }
  }

  if (building && building.antiquity) {
    initialAntiquity = BuildingFormJson.antiquity.find(c => c.value === building.antiquity)
    initialAntiquity = {
      label: initialAntiquity[lang],
      value: initialAntiquity.value
    }
  }

  if (building && building.airTightness) {
    initialAirThightness = BuildingFormJson.airTightness.find(
      c => c.value === building.airTightness
    )
    initialAirThightness = {
      label: initialAirThightness[lang],
      value: initialAirThightness.value
    }
  }

  const handleUseTypeChange = event => {
    setUseType(event.value)
    if (event.value === 'Residential') {
      setTypeBuildingOptionsSelect(typeBuildingResidentialOptions)
      setTypeVentilationOptions(ventilationResidentialOptions)
      setTypeOccupancyOptions(occupancyResidentialOptions)
    } else {
      setTypeBuildingOptionsSelect(typeBuildingTertiaryOptions)
      setTypeVentilationOptions(ventilationTertiaryOptions)
      setTypeOccupancyOptions(occupancyTertiaryOptions)
    }
  }
  const onClickCancel = () => {
    props.hideModal()
  }

  const onClickNext = () => {
    if (Object.keys(deviceList).length > 0) {
      clearErrors('noDevices')
      clearErrors('macAddressLength')
      clearErrors('name')
      clearErrors('noDeviceSelectedError')

      setActiveTab(1)
    } else {
      setError('noDevices', { shouldFocus: 'false' })
    }
  }

  const onClickBack = () => {
    setActiveTab(0)
  }
  const onClickAdd = () => {
    register('deviceList')
    setValue('deviceList', deviceList)
    setActiveTab(1)
  }

  const error = () => {
    if (!submitError) return null

    let message = translate('submitErrors.unknown')
    if (submitError.res && submitError.res.status === 409) {
      message = translate('submitErrors.systemAddError')
    } else if (submitError.res && submitError.res.status === 411) {
      message = translate('submitErrors.limitReached')
    }
    return (
      <Alert
        severity='error'
        className={styles.errorMessage}
      >
        {message}
      </Alert>
    )
  }

  const handleInfo = keyInfo => {
    const openDataType = openInfo[keyInfo]
    setOpenInfo({ ...openInfo, [keyInfo]: !openDataType })
    if (infoTab === keyInfo) {
      setInfoTab('')
    } else {
      setInfoTab(keyInfo)
    }
  }

  const closeInfo = keyInfo => {
    setOpenInfo({ ...openInfo, [keyInfo]: false })
    setInfoTab('')
  }
  const handleAddClick = () => {
    const nameIsEmpty = nameField === ''
    const identifierIssue =
      lookupType === DEVICE_LOOKUP_SN
        ? serialNumberIssue(macAddressField)
        : isDeviceIdentifierValid(lookupType, macAddressField)
        ? null
        : 'format'

    if (!nameIsEmpty && !identifierIssue) {
      clearErrors('macAddressLength')
      clearErrors('name')
      const { key, entry } = toDeviceListEntry(lookupType, macAddressField, nameField)
      setDeviceList(prev => ({
        ...prev,
        [key]: entry
      }))
      setNameField('')
      setMacAddress('')
      return
    }

    if (nameIsEmpty) {
      setError('name', { shouldFocus: 'false' })
    } else {
      clearErrors('name')
    }
    if (identifierIssue) {
      setError('macAddressLength', { type: identifierIssue, shouldFocus: 'false' })
    } else {
      clearErrors('macAddressLength')
    }
  }

  const handleChangeSelectedDevices = event => {
    const sensorId = event.target.id
    const isChecked = event.target.checked

    if (isChecked) {
      clearErrors('selectDevice')
      setSelectedDevices(prev => [...prev, sensorId])
    } else {
      setSelectedDevices(prev => prev.filter(id => id !== sensorId))
    }
  }

  const handleDeleteDevice = () => {
    if (selectedDevices.length === 0) {
      setError('noDeviceSelectedError', { shouldFocus: 'false' })
    } else {
      clearErrors('noDeviceSelectedError')

      setDeviceList(prevDeviceList => {
        const updatedDeviceList = Object.entries(prevDeviceList)
          .filter(([sensorId]) => !selectedDevices.includes(sensorId))
          .reduce((acc, [sensorId, device]) => {
            acc[sensorId] = device
            return acc
          }, {})

        return updatedDeviceList
      })

      setSelectedDevices([])
    }
  }

  const handleSelectAllDevices = event => {
    setIsCheckAllDevices(!isCheckAllDevices)
    setCheckDevicesAllIsClicked(true)
    if (isCheckAllDevices) {
      setSelectedDevices([])
    } else {
      const systemIds = []
      Object.keys(deviceList).map(s => {
        systemIds.push(s)
        return null
      })
      setSelectedDevices(systemIds)
      clearErrors('selectedDevices')
    }
  }
  const isIndeterminateDevices = () => {
    return (
      checkAllDevicesIsClicked &&
      selectedDevices.length > 0 &&
      selectedDevices.length !== Object.keys(deviceList).length
    )
  }
  const handleUpdatedSystem = id => {
    const { [id]: device, ...rest } = deviceList
    setLookupType(device.lookup === DEVICE_LOOKUP_SN ? DEVICE_LOOKUP_SN : DEVICE_LOOKUP_ID)
    setMacAddress(id)
    setNameField(device.name)
    setDeviceList(rest)
    clearErrors('macAddressLength')
    clearErrors('name')
  }

  const sendEmail = () => {
    window.location.href = `mailto:inbiotservices@inbiot.es`
  }
  // Device counters of the account. The API uses these same two numbers to accept
  // or refuse a new device, so we compare them here in the same way.
  const { isPremium, infoDevices } = user.toJS()
  const deviceLicenses = infoDevices?.nLimit
  const devicesInAccount = infoDevices?.nTotal ?? 0

  const isPremiumDeviceLimitReached =
    isPremium &&
    deviceLicenses != null &&
    devicesInAccount + Object.keys(deviceList).length >= deviceLicenses
  return (
    <div className={styles.modalContainer}>
      <Typography className={styles.header}>{translate('addSystems')}</Typography>
      <div className={styles.modal}>
        <form
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          onKeyDown={ev => {
            if (ev.key === 'Enter') {
              ev.preventDefault()
            }
          }}
        >
          <div
            className={styles.firstPage}
            style={{ display: activeTab === 0 ? 'block' : 'none' }}
          >
            <div className={styles.nav}>
              <Typography className={styles.subheaderDeviceSelected}>
                {translate('navDevice')}
              </Typography>
              <Typography className={styles.subheaderEnviroment}>
                {translate('navEnviroment')}
              </Typography>
            </div>
            <Typography className={styles.subTitle}>{translate('newDevice')}</Typography>
            <TextInput
              labelName={translate('nameSystem')}
              inputProps={{ maxLength: 36 }}
              value={nameField}
              handleChange={handleNameField}
              valueName='name'
              disabled={isPremiumDeviceLimitReached}
            />
            {errors.name && (
              <div className={styles.error}>
                <small>{translate('validation.required')}</small>
              </div>
            )}
            <div className={styles.deviceIdContainer}>
              <div
                className={cx(styles.lookupSelect, {
                  [styles.lookupSelectSn]: lookupType === DEVICE_LOOKUP_SN,
                  [styles.lookupSelectDisabled]: isPremiumDeviceLimitReached
                })}
              >
                <select
                  aria-label={translate('identifierType')}
                  value={lookupType}
                  disabled={isPremiumDeviceLimitReached}
                  onChange={event => handleLookupChange(event.target.value)}
                >
                  <option value={DEVICE_LOOKUP_ID}>ID</option>
                  <option value={DEVICE_LOOKUP_SN}>{translate('serialNumber')}</option>
                </select>
              </div>
              <div className={styles.identifierInput}>
                <TextInput
                  inputProps={{
                    maxLength: lookupType === DEVICE_LOOKUP_ID ? 14 : SERIAL_NUMBER_MAX_LENGTH
                  }}
                  value={macAddressField}
                  handleChange={handleIdentifierChange}
                  valueName='macAddress'
                  placeholder={lookupType === DEVICE_LOOKUP_ID ? 'XXXX-XXXX-XXXX' : 'XXXXXXXX'}
                  disabled={isPremiumDeviceLimitReached}
                />
              </div>
              <InfoIcon
                tooltipClassName={styles.deviceInfoTooltip}
                content={
                  <div className={styles.deviceInfo}>
                    <Typography className={styles.infoTitle}>{translate('deviceId')}</Typography>
                    <Typography className={styles.infoBody}>{translate('deviceIdInfo')}</Typography>
                    <Typography className={styles.infoBody}>
                      {translate('deviceIdFormat')}
                    </Typography>
                    <Typography className={styles.infoTitle}>
                      {translate('serialNumber')}
                    </Typography>
                    <Typography className={styles.infoBody}>
                      {translate('serialNumberInfo')}
                    </Typography>
                    <Typography className={styles.infoBody}>
                      {translate('serialNumberFormat')}
                    </Typography>
                    <a
                      className={styles.infoLink}
                      href={SERIAL_NUMBER_DOCS_URL}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      {translate('locateSerialNumberMac')}
                    </a>
                  </div>
                }
                icon={
                  <img
                    src='../images/info.svg'
                    className={cx('img_info', styles.infoIcon)}
                    alt='info'
                  />
                }
              />
            </div>
            {errors.macAddressLength && (
              <div className={styles.error}>
                <small>
                  {translate(
                    lookupType === DEVICE_LOOKUP_SN
                      ? errors.macAddressLength.type === 'digits'
                        ? 'validation.serialNumberDigits'
                        : 'validation.required'
                      : 'validation.invalidFormat'
                  )}
                </small>
              </div>
            )}
            {isPremiumDeviceLimitReached && (
              <div className={styles.licenseLimitReached}>
                <InfoIcon
                  content={
                    <>
                      <Typography className={styles.subTitle}>
                        {translate('AvailableLicenses')}
                      </Typography>
                      <Typography className={styles.infoText}>
                        {translate('hasLimit') +
                          ' ' +
                          deviceLicenses +
                          ' ' +
                          translate('devicelicenses')}
                      </Typography>
                      <Button
                        label={translate('constactUs')}
                        onClick={sendEmail}
                        type='next'
                        className={styles.infoButton}
                      />
                    </>
                  }
                  icon={<WarningAmberRoundedIcon className={styles.warninglicenseLimit} />}
                />
                <Typography className={styles.licenseLimitText}>
                  {translate('licenseLimitReached')}
                </Typography>
              </div>
            )}
            <Button
              label={translate('addDevice')}
              onClick={handleAddClick}
              type='add'
              className={styles.buttonWrapper}
              disabled={isPremiumDeviceLimitReached}
            />
            {Object.keys(deviceList).length > 0 && (
              <div className={styles.checkboxEmailWrapper}>
                <FormControlLabel
                  className={styles.selectAllCheckboxForm}
                  control={
                    <Checkbox
                      className={cx(styles.checkbox)}
                      classes={{ checked: styles.checkedBox }}
                      name='selectAllDevices'
                      id='selectAllDevices'
                      indeterminate={isIndeterminateDevices()}
                      onChange={handleSelectAllDevices}
                      checked={isCheckAllDevices}
                    />
                  }
                  label={translate('deviceToAdd')}
                />

                {Object.entries(deviceList).map(([sensorId, device]) => {
                  const deviceName = device.name
                  return (
                    <div
                      key={sensorId}
                      className={styles.checkboxFormDiv}
                    >
                      <div className={styles.checkboxForm}>
                        <Checkbox
                          className={cx(styles.checkbox)}
                          id={sensorId}
                          onChange={handleChangeSelectedDevices}
                          classes={{ checked: styles.checkedBox }}
                          required
                          checked={selectedDevices.includes(sensorId)}
                          name='emailRecipients'
                          value={deviceName}
                          inputRef={register()}
                          disableRipple
                        />
                        <span
                          className={
                            submitError?.res?.status === 409 && systemFailed?.includes(sensorId)
                              ? styles.checkboxLabelGray
                              : styles.checkboxLabel
                          }
                          onClick={() => handleUpdatedSystem(sensorId)}
                        >
                          {deviceName}
                        </span>
                      </div>
                      {submitError?.res?.status === 409 && systemFailed?.includes(sensorId) && (
                        <InfoIcon
                          content={
                            <>
                              <Typography className={styles.failedDevices}>
                                {translate('deviceAddFailure')}
                              </Typography>
                            </>
                          }
                          icon={<WarningAmberRoundedIcon className={styles.warning} />}
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            )}
            {errors.noDeviceSelectedError && (
              <div className={styles.error}>
                <small>{translate('validation.noDeviceSelectedError')}</small>
              </div>
            )}
            {Object.keys(deviceList).length > 0 && (
              <Button
                label={translate('deleteFromList')}
                onClick={handleDeleteDevice}
                type='light-delete'
                className={styles.buttonWrapper}
              />
            )}
            <Typography className={styles.subTitle}>{translate('asignDeviceProject')}</Typography>
            <div className={styles.selectProject}>
              <Controller
                className={styles.wrapperSelect}
                render={({ onChange, name }) => (
                  <div className={styles.custom}>
                    <Select
                      label={translate('selectedProject')}
                      handleChange={e => {
                        setValue(name, e.value)
                      }}
                      autoComplete='select project'
                      options={projectsOptions}
                      placeholder={translate('selectProject')}
                      className={cx(classes.selector, styles.selector, {
                        [styles.hasError]: errors.useType
                      })}
                    />
                  </div>
                )}
                control={control}
                name='selectedProject'
              />
            </div>
            {errors.noDevices && (
              <div className={styles.error}>
                <small>{translate('validation.atLeastOneDevice')}</small>
              </div>
            )}
            {submitError?.res?.status === 409 &&
              Object.keys(deviceList).some(item => systemFailed?.includes(item)) &&
              error()}
            <Button
              label={
                <>
                  {translate('next')}
                  <ArrowIcon className={styles.arrow} />
                </>
              }
              type='next'
              isLoading={isSubmitting}
              onClick={onClickNext}
              className={styles.buttonWrapper}
            />

            <Button
              type='cancel'
              isLoading={isSubmitting}
              onClick={onClickCancel}
              label={translate('cancel')}
              className={styles.buttonWrapper}
            />
          </div>
          <div
            className={styles.secondPage}
            style={{ display: activeTab === 1 ? 'block' : 'none' }}
          >
            <div className={styles.nav}>
              <Typography className={styles.subheaderDevice}>{translate('navDevice')}</Typography>
              <Typography className={styles.subheaderEnviromentSelected}>
                {translate('navEnviroment')}
              </Typography>
            </div>
            <div className={styles.infoPublicWrapper}>
              <Typography className={styles.buildingTypeInfoLabel}>
                {translate('buildingTypeInfo')}
              </Typography>
            </div>
            <div className={styles.cselect}>
              <Controller
                className={styles.wrapperSelect}
                render={({ onChange, name }) => (
                  <div className={styles.custom}>
                    <Select
                      label={translate('systemPage.typeOfUseBuilding')}
                      handleChange={e => {
                        handleUseTypeChange(e)
                        setValue(name, e.value)
                      }}
                      autoComplete='shipping useType'
                      error={errors.useType}
                      options={typeBuildingOptions}
                      placeholder={translate('systemPage.typeOfUseBuilding')}
                      className={cx(classes.selector, styles.selector, {
                        [styles.hasError]: errors.useType
                      })}
                    />
                  </div>
                )}
                control={control}
                name='building.useType'
              />
            </div>
            <div className={styles.moreInfoWrapper}>
              <Button
                onClick={() => handleInfo('typeOfUseBuilding')}
                type='info'
                label={translate('moreInfo')}
                className={styles.infoText}
              />
              <div
                style={{
                  display: infoTab === 'typeOfUseBuilding' ? 'block' : 'none'
                }}
                className={styles.caja}
              >
                <div className={styles.cajaHeader}>
                  <IconButton
                    disableRipple
                    aria-label='close'
                    className={styles.titleIconClose}
                    onClick={() => closeInfo('typeOfUseBuilding')}
                  >
                    <CloseIcon />
                  </IconButton>
                </div>
                {
                  <Translate
                    id='typeOfUseBuildingInfoN'
                    options={{ renderInnerHtml: true }}
                  />
                }
              </div>
            </div>
            <div className={styles.cselect}>
              <Controller
                className={styles.wrapperSelect}
                render={({ onChange, name }) => (
                  <div className={styles.custom}>
                    <Select
                      handleChange={e => {
                        setValue(name, e.value)
                      }}
                      autoComplete='shipping buildingType'
                      options={typeBuildingOptionsSelect}
                      placeholder={translate('systemPage.typeBuilding')}
                      disabled={!useType}
                      error={errors.buildingType}
                      defaultValue={initialBuildingType}
                      className={cx(classes.selector, styles.selector, {
                        [styles.hasError]: errors.building
                      })}
                    />
                  </div>
                )}
                control={control}
                name='building.buildingType'
              />
            </div>
            <div className={styles.cselect}>
              <Controller
                className={styles.wrapperSelect}
                render={({ onChange, name }) => (
                  <div className={styles.custom}>
                    <Select
                      handleChange={e => {
                        setValue(name, e.value)
                      }}
                      autoComplete='shipping ventilation'
                      options={typeVentilationOptions}
                      placeholder={translate('systemPage.ventilationBuilding')}
                      error={errors.ventilation}
                      disabled={!useType}
                      className={cx(classes.selector, styles.selector, {
                        [styles.hasError]: errors.ventilation
                      })}
                    />
                  </div>
                )}
                control={control}
                defaultValue={initialVentilation ? initialVentilation.value : null}
                name='building.ventilation'
              />
            </div>
            <div className={styles.moreInfoWrapper}>
              <Button
                type='info'
                onClick={() => handleInfo('ventilationBuilding')}
                label={translate('moreInfo')}
                className={styles.infoText}
              />
              <div
                style={{
                  display: infoTab === 'ventilationBuilding' ? 'block' : 'none'
                }}
                className={styles.caja}
              >
                <div className={styles.cajaHeader}>
                  <IconButton
                    disableRipple
                    aria-label='close'
                    className={styles.titleIconClose}
                    onClick={() => closeInfo('ventilationBuilding')}
                  >
                    <CloseIcon />
                  </IconButton>
                </div>
                {
                  <Translate
                    id='ventilationBuildingInfoN'
                    options={{ renderInnerHtml: true }}
                  />
                }
              </div>
            </div>
            <div className={styles.cselect}>
              <Controller
                className={styles.wrapperSelect}
                render={({ onChange, name }) => (
                  <div className={styles.custom}>
                    <Select
                      handleChange={e => {
                        setValue(name, e.value)
                      }}
                      autoComplete='shipping occupation'
                      options={typeOccupancyOptions}
                      disabled={!useType}
                      placeholder={translate('systemPage.occupancyBuilding')}
                      error={errors.occupation}
                      className={cx(styles.selector, {
                        [styles.hasError]: errors.occupation
                      })}
                    />
                  </div>
                )}
                control={control}
                defaultValue={initialOccupancy ? initialOccupancy.value : null}
                name='building.occupation'
              />
            </div>
            <div className={styles.moreInfoWrapper}>
              <Button
                type='info'
                onClick={() => handleInfo('occupancyBuilding')}
                label={translate('moreInfo')}
                className={styles.infoText}
              />
              <div
                style={{
                  display: infoTab === 'occupancyBuilding' ? 'block' : 'none'
                }}
                className={styles.caja}
              >
                <div className={styles.cajaHeader}>
                  <IconButton
                    disableRipple
                    aria-label='close'
                    className={styles.titleIconClose}
                    onClick={() => closeInfo('occupancyBuilding')}
                  >
                    <CloseIcon />
                  </IconButton>
                </div>
                {
                  <Translate
                    id='occupancyBuildingInfoN'
                    options={{ renderInnerHtml: true }}
                  />
                }
              </div>
            </div>
            <div className={styles.cselect}>
              <Controller
                className={styles.wrapperSelect}
                render={({ onChange, name }) => (
                  <div className={styles.custom}>
                    <Select
                      handleChange={e => {
                        setValue(name, e.value)
                      }}
                      autoComplete='shipping antiquity'
                      options={antiquityOptions}
                      placeholder={translate('systemPage.antiquityBuilding')}
                      error={errors.antiquity}
                      className={cx(classes.selector, styles.selector, {
                        [styles.hasError]: errors.building
                      })}
                    />
                  </div>
                )}
                control={control}
                defaultValue={initialAntiquity ? initialAntiquity.value : null}
                name='building.antiquity'
              />
            </div>
            <div className={styles.cselect}>
              <Controller
                className={styles.wrapperSelect}
                render={({ onChange, name }) => (
                  <div className={styles.custom}>
                    <Select
                      handleChange={e => {
                        setValue(name, e.value)
                      }}
                      autoComplete='shipping airTightness'
                      options={airtightnessOptions}
                      disabled={
                        !useType &&
                        !typeBuildingOptionsSelect &&
                        !typeVentilationOptions &&
                        !typeOccupancyOptions &&
                        !antiquityOptions
                      }
                      placeholder={translate('systemPage.airTightnessLevelBuilding')}
                      error={errors.airTightness}
                      className={cx(classes.selector, styles.selector, {
                        [styles.hasError]: errors.airTightness
                      })}
                    />
                  </div>
                )}
                control={control}
                defaultValue={initialAirThightness ? initialAirThightness.value : null}
                name='building.airTightness'
              />
            </div>
            <div className={styles.moreInfoWrapper}>
              <Button
                type='info'
                onClick={() => handleInfo('airTightnessLevelBuilding')}
                label={translate('moreInfo')}
                className={styles.infoText}
              />
              <div
                style={{
                  display: infoTab === 'airTightnessLevelBuilding' ? 'block' : 'none'
                }}
                className={styles.caja}
              >
                <div className={styles.cajaHeader}>
                  <IconButton
                    disableRipple
                    aria-label='close'
                    className={styles.titleIconClose}
                    onClick={() => closeInfo('airTightnessLevelBuilding')}
                  >
                    <CloseIcon />
                  </IconButton>
                </div>
                {
                  <Translate
                    id='airTightnessLevelBuildingInfoN'
                    options={{ renderInnerHtml: true }}
                  />
                }
              </div>
            </div>
            {submitError?.res?.status !== 409 && error()}
            <div className={styles.saveButton}>
              <Button
                type='next'
                submit
                isLoading={isSubmitting}
                onClick={onClickAdd}
                label={translate('save')}
              />
            </div>
            <div className={styles.buttonWrapper}>
              <Button
                type='cancel'
                onClick={onClickBack}
                label={
                  <>
                    {' '}
                    <ArrowIcon
                      className={styles.arrow}
                      direction='left'
                    />{' '}
                    {translate('back')}{' '}
                  </>
                }
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

AddSystemModalForm.propTypes = {
  isSubmitting: PropTypes.bool.isRequired,
  submitError: PropTypes.any,
  translate: PropTypes.func.isRequired,
  activeLanguage: PropTypes.object
}
export default withLocalize(AddSystemModalForm)
